/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { action } from '@ember/object';
import moment from 'moment-timezone';
import { isEmpty } from '@ember/utils';
import type Router from '@ember/routing/router-service';
import IntercomTimezoneOptions from 'embercom/lib/intercom-timezone-options';
import ReportingCustomReportShare from 'embercom/components/reporting/custom/report/share';
import type Report from 'embercom/models/reporting/custom/report';
import type IntlService from 'ember-intl/services/intl';
import type IntercomConfirmService from './intercom-confirm-service';
import type ReportAccessService from 'embercom/services/report-access-service';
import type Store from '@ember-data/store';
import type ReportingMetrics from 'embercom/services/reporting-metrics';

interface Timezonepayload {
  name: string;
  description: string;
  value: string;
}

export default class CustomReportsService extends Service {
  @service declare appService: AppService;
  @service declare router: Router;
  @service declare permissionsService: any;
  @service declare modalService: $TSFixMe;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare reportAccessService: ReportAccessService;
  @service declare store: Store;
  @service declare reportingMetrics: ReportingMetrics;
  get fallback(): Record<string, string> {
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    return { 'Europe/Kiev': 'Europe / Kyiv' };
  }

  get canChangeCustomReports() {
    if (this.appService.app.canShareReportsInternally) {
      return this.permissionsService.currentAdminCan('can_reporting__reports__update');
    }
    return this.permissionsService.currentAdminCan('can_reporting__custom_reports__change');
  }

  loadChangePermissionModal() {
    if (this.appService.app.canShareReportsInternally) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__reports__update',
      );
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__custom_reports__change',
      );
    }
  }

  @action
  async handleCreateReport() {
    if (!this.canChangeCustomReports) {
      this.loadChangePermissionModal();
    } else {
      this.router.transitionTo('apps.app.reports.custom-reports.report.new');
    }
  }

  timezones() {
    let momentTimezones = moment.tz.names();
    if (isEmpty(momentTimezones)) {
      return IntercomTimezoneOptions;
    }

    let formattedTimezones: Timezonepayload[] = [];
    let descriptions: string[] = [];
    momentTimezones.forEach((timezoneValue: string) => {
      let formattedName = timezoneValue
        .slice(timezoneValue.lastIndexOf('/') + 1)
        .replace(/_/g, ' ');
      let offset = moment
        .tz(timezoneValue)
        .format('[GMT]Z')
        .replace(/0(\d:)/, '$1')
        .replace(':00', '');

      let formattedDescription = this.formatDescription(offset, timezoneValue);
      if (!descriptions.includes(formattedDescription)) {
        descriptions.push(formattedDescription);
        formattedTimezones.push({
          name: formattedName,
          description: formattedDescription,
          value: timezoneValue,
        });
      }
    });
    return formattedTimezones;
  }

  formatDescription(offset: string, timezoneValue: string) {
    let fallbackValue = this.fallback[timezoneValue];
    return fallbackValue
      ? `(${offset}) ${fallbackValue}`
      : `(${offset}) ${timezoneValue.replace(/\//g, ' / ')}`;
  }

  get canDeleteReport() {
    if (this.appService.app.canShareReportsInternally) {
      return this.permissionsService.currentAdminCan('can_reporting__reports__delete');
    }
    return this.permissionsService.currentAdminCan('can_reporting__custom_reports__delete');
  }

  loadDeletePermissionsModal() {
    if (this.appService.app.canShareReportsInternally) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__reports__delete',
      );
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__custom_reports__delete',
      );
    }
  }

  openSharingModal(report: Report, isNewReport: boolean) {
    if (this.appService.app.canShareReportsInternally) {
      this.modalService.openModal(ReportingCustomReportShare, {
        report,
        newReportShareMode: isNewReport,
      });
    }
  }

  async confirmSave(includeSharingNote: boolean) {
    let confirmOptions = {
      title: this.intl.t('reporting.custom-reports.confirm-save.title'),
      primaryButtonType: 'primary',
      confirmButtonText: this.intl.t('reporting.custom-reports.confirm-save.confirm-button'),
      cancelButtonText: this.intl.t('reporting.custom-reports.confirm-save.cancel-button'),
      bodyComponentName: 'reporting/custom/confirm-save-body',
      confirmContext: {
        body: this.intl.t('reporting.custom-reports.confirm-save.body'),
        sharingNote:
          this.appService.app.canShareReportsInternally && includeSharingNote
            ? this.intl.t('reporting.custom-reports.confirm-save.sharing-note')
            : null,
      },
      distinguishCancelFromClose: true,
    };
    return await this.intercomConfirmService.confirm(confirmOptions);
  }

  async saveDefaultReportAccess(report: Report) {
    await this.reportAccessService.saveDefaultWorkspaceAccess(report);
  }

  generateID() {
    return `new-${new Date().valueOf()}`;
  }

  routeToOverviewPage() {
    this.router.transitionTo('apps.app.reports.overview');
  }

  async preloadDescriptorsAndTicketTypes() {
    let promises = [
      this.store.findAll('conversation-attributes/descriptor', {
        backgroundReload: false,
      }),
      this.reportingMetrics.loadMetricsAndProperties(),
      this.store.findAll('inbox/ticket-type', { reload: true }),
    ];

    return Promise.all(promises);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'custom-reports-service': CustomReportsService;
  }
}

declare module '@ember/service' {
  interface Registry {
    customReportsService: CustomReportsService;
    'custom-reports-service': CustomReportsService;
  }
}
